import { paths } from 'src/routes/paths';
import type { TFunction } from 'i18next';

import { Iconify } from 'src/components/iconify';
import { Permission } from 'src/assets/data/permissions';

export function navData(t: TFunction<any, any>, showCommunication = false, showAnalytics = false) {
  return [
    {
      subheader: t('common.workspace'),
      items: [
        {
          title: t('sidebar.menuItems.dashboard'),
          path: paths.dashboard.root,
          icon: <Iconify width={18} icon="lucide:layout-dashboard" />,
          permissions: [Permission.ReadDashboard],
          dataTestId: 'menu-item-dashboard',
        },
        {
          title: t('sidebar.menuItems.invoices'),
          path: paths.dashboard.invoices,
          icon: <Iconify width={18} icon="lucide:receipt" />,
          permissions: [Permission.ReadInvoice],
          dataTestId: 'menu-item-invoices',
        },
        {
          title: t('sidebar.menuItems.customers'),
          path: paths.dashboard.customers,
          icon: <Iconify width={18} icon="lucide:users" />,
          permissions: [Permission.ReadCustomer],
          dataTestId: 'menu-item-customers',
        },
        {
          title: t('sidebar.menuItems.paymentManagement'),
          path: paths.dashboard.payments.paymentsManagement,
          icon: <Iconify width={18} icon="lucide:wallet" />,
          permissions: [Permission.ReadPayment],
          dataTestId: 'menu-item-payments',
          customMaxLine: 2,
          children: [
            {
              title: t('sidebar.menuItems.payments'),
              path: paths.dashboard.payments.paymentsManagement,
              icon: <Iconify width={18} icon="lucide:credit-card" />,
              permissions: [Permission.ReadPayment],
              dataTestId: 'menu-item-payment-management',
            },
            {
              title: t('sidebar.menuItems.directDebitSubscriptions'),
              path: paths.dashboard.payments.directDebitSubscriptions,
              icon: <Iconify width={18} icon="lucide:receipt-text" />,
              permissions: [Permission.DirectPayment],
              dataTestId: 'menu-item-direct-debit-subscriptions',
              customMaxLine: 2,
            },
          ],
        },
        ...(showAnalytics
          ? [
              {
                title: t('sidebar.menuItems.analytics'),
                path: paths.dashboard.analytics,
                icon: <Iconify width={18} icon="lucide:chart-pie" />,
                permissions: [Permission.ReadDashboard],
                dataTestId: 'menu-item-analytics',
              },
            ]
          : []),
        {
          title: t('sidebar.menuItems.reports'),
          path: paths.dashboard.reports,
          icon: <Iconify width={18} icon="lucide:file-text" />,
          permissions: [Permission.ReadReport],
          dataTestId: 'menu-item-reports',
        },
        ...(showCommunication
          ? [
              {
                title: t('sidebar.menuItems.communication'),
                path: '/communication',
                icon: <Iconify width={18} icon="lucide:message-circle" />,
                permissions: [Permission.ReadEmail],
                dataTestId: 'menu-item-communication',
                children: [
                  {
                    title: t('sidebar.menuItems.unassignedEmails'),
                    path: paths.dashboard.communication.unassignedEmails,
                    icon: <Iconify width={18} icon="lucide:mail" />,
                    permissions: [Permission.ReadEmail],
                    dataTestId: 'menu-item-unassigned-emails',
                  },
                ],
              },
            ]
          : []),
        // {
        //   title: t('sidebar.menuItems.reports'),
        //   path: paths.dashboard.reports,
        //   icon: <Iconify width={18} icon="lucide:file-text" />,
        //   roles: [UserRoles.CLIENT_MANAGER, UserRoles.CLIENT_OPERATOR, UserRoles.CUSTOMER_OPERATOR],
        //   dataTestId: 'menu-item-reports',
        // },
        // { title: t('sidebar.menuItems.imports'), path: paths.dashboard.imports, icon: <Iconify width={18} icon="lucide:import" /> },
        // { title: t('sidebar.menuItems.failures'), path: paths.dashboard.failures, icon: <Iconify width={18} icon="lucide:ban" />},
        // { title: t('sidebar.menuItems.analytics'), path: paths.dashboard.analytics, icon: <Iconify width={18} icon="lucide:chart-no-axes-combined" /> },
      ],
    },
    {
      subheader: t('common.management'),
      items: [
        {
          title: t('sidebar.menuItems.users'),
          path: paths.dashboard.users,
          icon: <Iconify width={18} icon="lucide:users" />,
          permissions: [Permission.ReadUser],
          dataTestId: 'menu-item-users',
        },
        {
          title: t('sidebar.menuItems.clients'),
          path: paths.dashboard.clients.client,
          icon: <Iconify width={18} icon="mdi:account-box-outline" />,
          permissions: [Permission.ReadClient],
          dataTestId: 'menu-item-clients',
          children: [
            {
              title: t('sidebar.menuItems.clients'),
              path: paths.dashboard.clients.client,
              icon: <Iconify width={18} icon="lucide:user-cog" />,
              permissions: [Permission.ReadClient],
              dataTestId: 'menu-item-clients',
            },
            {
              title: t('sidebar.menuItems.serviceLine'),
              path: paths.dashboard.clients.serviceLine,
              icon: <Iconify width={18} icon="teenyicons:curved-connector-outline" />,
              permissions: [Permission.ReadClient],
              dataTestId: 'menu-item-service-line',
            },
          ],
        },
        {
          title: t('sidebar.menuItems.webhooks'),
          path: paths.dashboard.webhooks.subscriptions,
          icon: <Iconify width={18} icon="lucide:webhook" />,
          dataTestId: 'menu-item-webhooks',
          permissions: [Permission.ReadWebhooks, Permission.ReadWebhooksHistory],
          children: [
            {
              title: t('sidebar.menuItems.webhookSubscriptions'),
              path: paths.dashboard.webhooks.subscriptions,
              icon: <Iconify width={18} icon="lucide:network" />,
              permissions: [Permission.ReadWebhooks],
              dataTestId: 'menu-item-subscriptions',
            },
            {
              title: t('sidebar.menuItems.webhookEvents'),
              path: paths.dashboard.webhooks.history,
              icon: <Iconify width={18} icon="lucide:history" />,
              permissions: [Permission.ReadWebhooksHistory],
              dataTestId: 'menu-item-history',
            },
          ],
        },
        {
          title: t('sidebar.menuItems.settings'),
          path: paths.dashboard.settings.oidcClient,
          icon: <Iconify width={18} icon="lucide:settings-2" />,
          permissions: [Permission.ReadOidcClient],
          dataTestId: 'menu-item-settings',
          children: [
            {
              title: t('sidebar.menuItems.oidcClient'),
              path: paths.dashboard.settings.oidcClient,
              icon: <Iconify width={18} icon="lucide:key-round" />,
              permissions: [Permission.ReadOidcClient],
              dataTestId: 'menu-item-odic-client',
            },
          ],
        },
      ],
    },
  ];
}
